import { Route, Router, Routes } from '@angular/router';

import { AuthenticationGuard, PERMISSIONS, PermissionService } from '@app/auth';
import { ShellComponent } from '@app/shell/shell.component';
import { BehaviorSubject, debounceTime, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { isMobileView } from '@core/utils';
import { PlatformRouteGuard } from '@core/guards';
import { NavMenuItem } from '@core/types';

/**
 * Provides helper methods to create routes.
 */
export class Shell {
  /**
   * Creates routes using the shell component and authentication.
   * @param routes The routes to add.
   * @return The new route using shell as the base.
   */
  static childRoutes(routes: Routes): Route {
    return {
      path: '',
      component: ShellComponent,
      children: routes,
      canActivate: [AuthenticationGuard],
      canActivateChild: [PlatformRouteGuard],
      data: { reuse: true },
    };
  }
}

/**
 * Helper class for navigation route matching and active state management
 */
class NavRouteHelper {
  /**
   * Special route patterns that need custom matching logic
   */
  private static readonly _routePatterns = {
    clubOverview: { pattern: /^club-overview/, matchPaths: ['/club-overview/news'] },
    mobileClubWall: { pattern: /^m\/club-wall/, matchPaths: ['/m/club-wall/events'] },
  };

  /**
   * Check if a menu item should be active based on current URL
   */
  static isRouteActive(itemHref: string, currentUrl: string): boolean {
    if (!itemHref) return false;

    // Handle special 'add' button case
    if (itemHref === 'add') return false;

    const urlSegments = this._getUrlSegments(currentUrl);
    const hrefSegments = this._getUrlSegments(itemHref);

    // Check special patterns first
    if (this._matchesSpecialPattern(itemHref, urlSegments)) {
      return true;
    }

    // Default matching: check if all href segments match the beginning of url segments
    return this._segmentsMatch(hrefSegments, urlSegments);
  }

  /**
   * Extract URL segments from a path
   */
  private static _getUrlSegments(url: string): string[] {
    return url.split('/').filter((segment) => segment.length > 0);
  }

  /**
   * Check if URL matches any special patterns
   */
  private static _matchesSpecialPattern(itemHref: string, urlSegments: string[]): boolean {
    // Club overview pattern
    if (this._routePatterns.clubOverview.matchPaths.includes(itemHref)) {
      return urlSegments[0] === 'club-overview';
    }

    // Mobile club wall pattern
    if (this._routePatterns.mobileClubWall.matchPaths.includes(itemHref)) {
      return urlSegments.length >= 2 && urlSegments[0] === 'm' && urlSegments[1] === 'club-wall';
    }

    return false;
  }

  /**
   * Check if href segments match the beginning of url segments
   */
  private static _segmentsMatch(hrefSegments: string[], urlSegments: string[]): boolean {
    return hrefSegments.every((segment, i) => segment === urlSegments[i]);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ShellService {
  readonly _permissionService = inject(PermissionService);
  navicon = new BehaviorSubject<NavMode>(NavMode.Free);
  navModeSubject = new BehaviorSubject<NavMode>(NavMode.Free);
  navMode$ = this.navModeSubject.asObservable();
  navicon$ = this.navModeSubject.asObservable();
  // Resize window event handler
  isMobileViewSubject = new BehaviorSubject<boolean>(isMobileView());
  isMobileView$: Observable<boolean> = this.isMobileViewSubject.asObservable().pipe(debounceTime(300));
  private readonly _router = inject(Router);

  constructor() {
    window.addEventListener('resize', this._onResize.bind(this));
  }

  allowedAccess(item: NavMenuItem): boolean {
    if (item.roles && item.roles.length) {
      return item.roles.includes(this._permissionService.userRole);
    }

    if (item.notAllowedRoles && item.notAllowedRoles.length) {
      return !item.notAllowedRoles.includes(this._permissionService.userRole);
    }

    if (item.permissions && item.permissions.length) {
      if (item.permissions.includes(PERMISSIONS.VCLOUD_COURSE)) {
        return this._permissionService.isCourseAccessAllowed();
      }

      if (item.permissions.includes(PERMISSIONS.VCLOUD_WORK_SHIFTS)) {
        return this._permissionService.isWorkShiftsAccessAllowed();
      }

      return item.permissions.some((permission: PERMISSIONS) => this._permissionService.hasPermission(permission));
    }

    return true;
  }

  toggleNavMode(): void {
    const mode = this.navModeSubject.getValue();
    this.navModeSubject.next(mode === NavMode.Free ? NavMode.Locked : NavMode.Free);
    this.navicon.next(mode === NavMode.Free ? NavMode.Locked : NavMode.Free);
  }

  /**
   * Updates the active state of navigation items based on current route
   * @param items - Navigation menu items to update
   */
  activeNavTab(items: NavMenuItem[]): void {
    const currentUrl = this._router.url;

    items.forEach((item) => {
      // Update main item active state
      item.active = item.href ? NavRouteHelper.isRouteActive(item.href, currentUrl) : false;

      // Update sub-items active state
      if (item.subItems?.length) {
        item.subItems.forEach((subItem) => {
          subItem.active = subItem.href ? NavRouteHelper.isRouteActive(subItem.href, currentUrl) : false;
        });
      }
    });
  }

  /**
   * Activates a navigation item and handles navigation
   * @param index - Index of the item to activate
   * @param navItems - Array of navigation items
   */
  activateNavItem(index: number, navItems: NavMenuItem[]): void {
    const item = navItems[index];
    if (!item || item.disabled) return;

    // Scroll to item if needed
    this._scrollToNavItem(index);

    // If item has no sub-items, navigate to its href
    if (!item.subItems?.length && item.href) {
      this._router.navigate([item.href]);
    } else if (item.subItems?.length) {
      // If item has sub-items, deactivate all sub-items of all items
      this._deactivateAllSubItems(navItems);
    }
  }

  /**
   * Activates a navigation sub-item and handles navigation
   * @param parentIndex - Index of the parent item
   * @param subItem - Sub-item to activate
   * @param navItems - Array of navigation items
   */
  activateNavSubItem(parentIndex: number, subItem: NavMenuItem, navItems: NavMenuItem[]): void {
    if (!subItem || subItem.disabled) return;

    const parentItem = navItems[parentIndex];
    if (!parentItem) return;

    // Activate parent and selected sub-item
    parentItem.active = true;
    subItem.active = true;

    // Deactivate all other sub-items of the parent
    parentItem.subItems?.forEach((item) => {
      if (item !== subItem) {
        item.active = false;
      }
    });

    // Navigate or open URL
    if (subItem.href) {
      this._router.navigate([subItem.href]);
    } else if (subItem.url) {
      window.open(subItem.url, '_blank');
    }
  }

  /**
   * Get the current active route
   * @param lastSegmentOnly - If true, returns only the last segment of the URL
   */
  getCurrentActiveRoute(lastSegmentOnly = true): string {
    const url = this._router.url;
    const urlSegments = url.split('/');
    const lastSegment = urlSegments[urlSegments.length - 1];
    return lastSegmentOnly ? lastSegment : url;
  }

  /**
   * Scrolls navigation to center the selected item
   * @param index - Index of the item to scroll to
   */
  private _scrollToNavItem(index: number): void {
    setTimeout(() => {
      const element = document.getElementById(`menu-item-${index}`);
      const navElement = document.querySelector('nav');

      if (element && navElement) {
        const elementRect = element.getBoundingClientRect();
        const navRect = navElement.getBoundingClientRect();

        const relativeTop = elementRect.top - navRect.top;
        const desiredScrollPosition = navElement.scrollTop + relativeTop - navRect.height / 2;

        navElement.scrollTo({ top: desiredScrollPosition, behavior: 'smooth' });
      }
    }, 0);
  }

  /**
   * Deactivates all sub-items across all navigation items
   * @param navItems - Array of navigation items
   */
  private _deactivateAllSubItems(navItems: NavMenuItem[]): void {
    navItems.forEach((item) => {
      item.subItems?.forEach((subItem) => {
        subItem.active = false;
      });
    });
  }

  private _onResize(): void {
    this.isMobileViewSubject.next(isMobileView());
  }
}

export enum NavMode {
  Locked,
  Free,
}
